import React, {
  ReactNode, useCallback, useId, useMemo, useState,
} from 'react';
import { Modal, Spinner } from 'react-bootstrap';

import Button from 'components/DesignSystem/Button';
import QueryErrorState from 'components/DesignSystem/QueryErrorState';
import TextArea from 'components/DesignSystem/TextArea';
import ConfirmationDialogBox from 'components/ConfirmationDialogBox';

import { formatarDataHoraCurta } from 'helper/Relatorios/Aprovacao';
import { DESSEM_CENARIO_LABEL } from 'helper/Relatorios/Aprovacao/config';
import { rotuloDataDoInforme } from 'helper/Relatorios/Dessem';
import { ResponseError } from 'services/api/base';
import {
  useEditarDessemPremissasMutation,
  useGetDessemPremissasEdicaoQuery,
  useRestaurarDessemPremissasMutation,
} from 'services/api/products/relatorios';

import { DessemPremissasBloqueio, DessemSecaoParams } from 'types/Relatorios/Dessem';

import './index.scss';

/** Teto do texto, o mesmo do backend (`PREMISSAS_MAX_CHARS`, contrato §2.15). */
const PREMISSAS_MAX_CARACTERES = 64000;

/** Perto do limite (90% do teto, 57.600) o contador de caracteres aparece abaixo do campo. */
const CONTADOR_A_PARTIR_DE = PREMISSAS_MAX_CARACTERES * 0.9;

const PAR_DE_SURROGATES = /[\uD800-\uDBFF][\uDC00-\uDFFF]/g;

/**
 * Caracteres contados como o backend (`len` do Python, pontos de código): um emoji é 1 caractere
 * para o limite, mas 2 unidades no `length` do JavaScript.
 */
const contarCaracteres = (texto: string): number => (
  texto.length - (texto.match(PAR_DE_SURROGATES)?.length ?? 0)
);

const formatarNumero = (valor: number): string => valor.toLocaleString('pt-BR');

/**
 * Quebras de linha no formato do `value` do textarea (CRLF e CR soltos viram LF): trocar só o tipo
 * da quebra não conta como alteração do texto.
 */
const normalizarQuebras = (texto: string): string => texto.replace(/\r\n?/g, '\n');

/** `['early', 'preliminar']` -> `'Early e Preliminar'` (rótulos dos radios; senão, o código). */
const listarEtapas = (cenarios: string[]): string => {
  const rotulos = cenarios.map((cenario) => DESSEM_CENARIO_LABEL[cenario] ?? cenario);
  if (rotulos.length <= 1) return rotulos.join('');
  return `${rotulos.slice(0, -1).join(', ')} e ${rotulos[rotulos.length - 1]}`;
};

/** Aviso do editor bloqueado (`pode_editar: false`): o que o admin precisa fazer para editar. */
export const mensagemDoBloqueio = (bloqueio: DessemPremissasBloqueio): string => (
  bloqueio.motivo === 'publicado'
    ? 'Informe publicado: reabra para editar.'
    : `Premissas aprovadas em ${listarEtapas(bloqueio.cenarios)}: revogue a aprovação para editar.`
);

/** `description` do envelope de erro do backend (o baseQuery a guarda no erro), se houver. */
const descricaoDoErro = (erro: unknown): string | null => {
  if (typeof erro !== 'object' || erro === null) return null;
  const { description } = erro as Partial<ResponseError>;
  return typeof description === 'string' && description.trim() !== '' ? description : null;
};

interface EditorDasPremissasProps {
  params: DessemSecaoParams;
  enviando: boolean;
  salvar: (texto: string) => Promise<unknown>;
  restaurar: () => Promise<unknown>;
  onCancelar: () => void;
  onConcluido: () => void;
}

/**
 * Corpo e rodapé do modal. Monta só com o modal aberto: cada abertura busca o estado do editor de
 * novo (bloqueio e autoria atuais) e começa sem rascunho nem erro.
 */
function EditorDasPremissas({
  params, enviando, salvar, restaurar, onCancelar, onConcluido,
}: EditorDasPremissasProps) {
  const campoId = useId();
  const avisosId = useId();
  const ajudaId = useId();
  const {
    currentData: edicao, isError, error, refetch,
  } = useGetDessemPremissasEdicaoQuery(params);
  // `null` até o admin mexer no campo: até lá vale o texto que veio do backend.
  const [rascunho, setRascunho] = useState<string | null>(null);
  const [erroAcao, setErroAcao] = useState<string | null>(null);
  const [confirmandoRestauracao, setConfirmandoRestauracao] = useState(false);

  const texto = rascunho ?? edicao?.conteudo_markdown ?? '';
  const caracteres = useMemo(() => contarCaracteres(texto), [texto]);
  const emBranco = texto.trim() === '';
  const acimaDoLimite = caracteres > PREMISSAS_MAX_CARACTERES;
  const podeEditar = edicao?.pode_editar === true;
  // Salvar só com o texto alterado (Marcelo, 16/10/2026): gravar o texto do estudo sem mexer o
  // fixaria nas 3 etapas do dia (o da Early, que cita "EARLY", iria para a Preliminar e o POS_ONS),
  // e regravar um texto editado igual só criaria eventos "editado".
  const alterado = rascunho !== null
    && normalizarQuebras(rascunho) !== normalizarQuebras(edicao?.conteudo_markdown ?? '');
  const podeSalvar = podeEditar && alterado && !emBranco && !acimaDoLimite && !enviando;

  const executar = useCallback((acao: () => Promise<unknown>, mensagemPadrao: string) => {
    setErroAcao(null);
    acao()
      .then(() => onConcluido())
      .catch((erro: unknown) => setErroAcao(descricaoDoErro(erro) ?? mensagemPadrao));
  }, [onConcluido]);

  // O texto vai como está, sem `trim` (o backend guarda exatamente o que recebe).
  const handleSalvar = useCallback(() => executar(
    () => salvar(texto),
    'Não foi possível salvar o texto. Tente novamente.',
  ), [executar, salvar, texto]);

  const handleRestaurar = useCallback(() => executar(
    restaurar,
    'Não foi possível restaurar o texto do estudo. Tente novamente.',
  ), [executar, restaurar]);

  const abrirConfirmacao = useCallback(() => setConfirmandoRestauracao(true), []);
  const fecharConfirmacao = useCallback(() => setConfirmandoRestauracao(false), []);
  // Mexer no texto tira o erro da última tentativa (ex.: 422), que já não descreve o texto atual.
  const handleChange = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setRascunho(event.target.value);
    setErroAcao(null);
  }, []);

  let corpo: ReactNode;
  if (edicao) {
    const mostrarContador = caracteres >= CONTADOR_A_PARTIR_DE;
    corpo = (
      <>
        <p className="dessem-premissas-edicao__data">
          {`Informe de ${rotuloDataDoInforme(params.data_rodada)}`}
        </p>
        <div id={avisosId} className="dessem-premissas-edicao__avisos">
          <p className="dessem-premissas-edicao__aviso">O texto vale para as 3 etapas do dia.</p>
          {edicao.bloqueio && (
            <p className="dessem-premissas-edicao__aviso dessem-premissas-edicao__aviso--bloqueio">
              {mensagemDoBloqueio(edicao.bloqueio)}
            </p>
          )}
          {edicao.origem === 'edicao' && edicao.ultima_edicao && (
            <p className="dessem-premissas-edicao__autoria">
              {`Editado por ${edicao.ultima_edicao.por} em ${formatarDataHoraCurta(edicao.ultima_edicao.em)}`}
            </p>
          )}
        </div>
        {/* Markdown cru no valor do campo: nunca vira HTML aqui (o HTML seguro é o da seção). */}
        <TextArea
          controlId={campoId}
          label="Texto em Markdown"
          value={texto}
          onChange={handleChange}
          rows={14}
          readOnly={!podeEditar}
          autoFocus
          aria-describedby={`${avisosId} ${ajudaId}`}
          className="dessem-premissas-edicao__campo"
        />
        <div id={ajudaId} className="dessem-premissas-edicao__ajuda">
          {emBranco && <p>O texto não pode ficar em branco.</p>}
          {mostrarContador && (
            <p className={acimaDoLimite ? 'dessem-premissas-edicao__excedido' : undefined}>
              {`${formatarNumero(caracteres)} / ${formatarNumero(PREMISSAS_MAX_CARACTERES)} caracteres`}
            </p>
          )}
          {acimaDoLimite && (
            <p className="dessem-premissas-edicao__excedido">
              {`O texto passou do limite de ${formatarNumero(PREMISSAS_MAX_CARACTERES)} caracteres.`}
            </p>
          )}
        </div>
      </>
    );
  } else if (isError) {
    corpo = (
      <QueryErrorState
        title="Não foi possível carregar o texto das Premissas Gerais"
        message={descricaoDoErro(error) ?? 'Tente novamente em alguns instantes.'}
        onRetry={refetch}
      />
    );
  } else {
    corpo = (
      <div className="d-flex justify-content-center p-4">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Carregando o texto das Premissas Gerais</span>
        </Spinner>
      </div>
    );
  }

  return (
    <>
      <Modal.Body className="dessem-premissas-edicao__corpo">
        {corpo}
        {erroAcao && <p role="alert" className="dessem-premissas-edicao__erro">{erroAcao}</p>}
      </Modal.Body>
      <Modal.Footer className="dessem-premissas-edicao__rodape">
        {edicao?.origem === 'edicao' && (
          <Button
            type="tertiary"
            small
            className="dessem-premissas-edicao__restaurar"
            onClick={abrirConfirmacao}
            disabled={!podeEditar || enviando}
          >
            Restaurar texto do estudo
          </Button>
        )}
        <Button type="secondary" small onClick={onCancelar} disabled={enviando}>
          Cancelar
        </Button>
        <Button small onClick={handleSalvar} disabled={!podeSalvar}>
          Salvar
        </Button>
      </Modal.Footer>
      <ConfirmationDialogBox
        open={confirmandoRestauracao}
        confirm={handleRestaurar}
        dismiss={fecharConfirmacao}
        title="Restaurar o texto do estudo?"
        confirmLabel="Restaurar"
        dismissLabel="Voltar"
      >
        O texto editado será apagado e as Premissas Gerais das 3 etapas do dia voltam ao texto do
        estudo.
      </ConfirmationDialogBox>
    </>
  );
}

export interface PremissasEdicaoModalProps {
  open: boolean;
  onClose: () => void;
  /** Data do Informe e etapa da tela; o texto gravado vale para as 3 etapas do dia. */
  params: DessemSecaoParams;
}

/**
 * Edição do texto das Premissas Gerais do dia pelo admin (contrato §2.15 do BE). Abre com o texto
 * em Markdown do estado do editor (`GET aprovacao/premissas`): o do estudo ou o já editado, com a
 * autoria. Salvar (só com o texto alterado) grava o texto para as 3 etapas do dia; "Restaurar texto
 * do estudo" (só com texto editado) apaga a edição, depois de confirmar. Com o editor bloqueado
 * (Premissas aprovadas e não publicadas em alguma etapa, ou etapa da tela publicada), avisa o que
 * fazer e desabilita as ações.
 * Erro do backend aparece dentro do modal, que não fecha. No celular (<768 px) ocupa a tela.
 *
 * As mutations ficam aqui, e não no corpo: com uma gravação a caminho, Esc, o X e Cancelar não
 * fecham, para o admin ver se o texto foi salvo. O fundo não fecha o modal (o texto digitado não se
 * perde num clique fora); Esc fecha sem salvar.
 */
export default function PremissasEdicaoModal({ open, onClose, params }: PremissasEdicaoModalProps) {
  const tituloId = useId();
  const [editar, { isLoading: salvando }] = useEditarDessemPremissasMutation();
  const [restaurar, { isLoading: restaurando }] = useRestaurarDessemPremissasMutation();
  const enviando = salvando || restaurando;

  const fechar = useCallback(() => {
    if (!enviando) onClose();
  }, [enviando, onClose]);

  const salvar = useCallback((texto: string) => (
    editar({ ...params, conteudo_markdown: texto }).unwrap()
  ), [editar, params]);

  const restaurarTextoDoEstudo = useCallback(() => restaurar(params).unwrap(), [restaurar, params]);

  return (
    <Modal
      show={open}
      onHide={fechar}
      size="lg"
      fullscreen="md-down"
      centered
      backdrop="static"
      aria-labelledby={tituloId}
      className="dessem-premissas-edicao"
    >
      <Modal.Header closeButton closeLabel="Fechar">
        <Modal.Title as="h2" id={tituloId} className="dessem-premissas-edicao__titulo">
          Editar Premissas Gerais
        </Modal.Title>
      </Modal.Header>
      <EditorDasPremissas
        params={params}
        enviando={enviando}
        salvar={salvar}
        restaurar={restaurarTextoDoEstudo}
        onCancelar={fechar}
        onConcluido={onClose}
      />
    </Modal>
  );
}
